import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import apis from "#store/api";
import { DataApiResponse, DataCollectionApiResponse } from "./types";
import { ApiSettingKey } from "#pages/Home/OrganizationForm";

export const TENANT_SETTINGS_KEY = "tenantSettings";

export interface TenantSettingDto {
    id?: string;
    name: string;
    value: string;
}

export const TENANT_API_SETTING_NAMES: Record<ApiSettingKey, string> = {
    metroPcrVictims: "MetroPcrVictimsActive",
    metroPcrContacts: "MetroPcrContactsActive",
    webEocVictims: "WebEocVictimsActive"
};

export type ApiActiveSettings = Record<ApiSettingKey, boolean>;

const parseBool = (value?: string): boolean =>
    !!value && value.toLowerCase() === "true";

interface UseTenantSettingsOptions {
    refetchOnMount?: boolean | 'always';
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number | false;
    staleTime?: number;
}

export const useTenantSettings = (tenantId?: string, options: UseTenantSettingsOptions = {}) => {
    return useQuery({
        queryKey: [TENANT_SETTINGS_KEY, tenantId],
        queryFn: async () => {
            const response = await apis.tenantSettings.getTenantSettings({ data: { tenantId } });
            return response.data as DataCollectionApiResponse<TenantSettingDto>;
        },
        enabled: !!tenantId,
        ...options
    });
};

export const useTenantApiActiveSettings = (tenantId?: string, options: UseTenantSettingsOptions = {}) => {
    const query = useTenantSettings(tenantId, options);

    const active = useMemo<ApiActiveSettings>(() => {
        const result: ApiActiveSettings = {
            metroPcrVictims: false,
            metroPcrContacts: false,
            webEocVictims: false
        };

        query.data?.items?.forEach(setting => {
            (Object.keys(TENANT_API_SETTING_NAMES) as ApiSettingKey[]).forEach(key => {
                if (setting.name === TENANT_API_SETTING_NAMES[key]) {
                    result[key] = parseBool(setting.value);
                }
            });
        });

        return result;
    }, [query.data]);

    return { ...query, data: active };
};

export const useUpdateTenantSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: { tenantId: string; settings: TenantSettingDto[] }) => {
            const response = await apis.tenantSettings.updateTenantSettings({ data: params });
            return response.data as DataApiResponse<TenantSettingDto[]>;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [TENANT_SETTINGS_KEY, variables.tenantId] });
        }
    });
};

export const useRunTenantIntegration = () =>
    useMutation({
        mutationFn: async (tenantId: string) => {
            const response = await apis.tenantSettings.runIntegration({ data: { tenantId } });
            return response.data as DataApiResponse<{ active: boolean }>;
        }
    });
